import { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import { actionBound, observable, runInAction } from 'mobx';
import { stringify } from 'qs';
import { UiResponse } from '../CommonInterfaces';
import { LARES_POST_FORM_PATH, View } from '../Routes';
import ErrorUtil from '../util/ErrorUtil';
import { extractHyperlink, getPathForComponent, isSameProtocolAndHost } from '../util/NavigationUtil';
import GaDataStore from './GaDataStore';
import MergeStore from './MergeStore';
import PasskeyStore from './PasskeyStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';
import { API } from '../util/ApiUtil';
import Config from '../Config';
import RememberMeStore from './RememberMeStore';

export enum ProfileMapper {
  MERGE_ACCESS_VERIFIED = 'MERGE_ACCESS_VERIFIED',
  MERGE_TARGET_IDENTIFIED = 'MERGE_TARGET_IDENTIFIED',
}

export default abstract class BaseLoginStore {
  @observable accessor submitting: boolean = false;

  protected routeStore: RouteStore;
  protected uiStore: UiStore;
  protected rememberMeStore: RememberMeStore;
  protected mergeStore: MergeStore;
  protected passkeyStore: PasskeyStore;

  protected constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.rememberMeStore = rootStore.rememberMeStore;
    this.mergeStore = rootStore.mergeStore;
    this.passkeyStore = rootStore.passkeyStore;
  }

  protected abstract handleLogin(...args: any[]): void;

  protected async handleLoginTemplate(url: string, fields: object, offerPasskeyRegister: boolean, config?: AxiosRequestConfig, callback?: () => void) {
    this.resetState();
    const authType = url.includes('/email/auth') ? 'email' : undefined;
    try {
      const response = await API.post(`${url}${this.routeStore.formForwardParams}`, fields, config);
      await this.handleSuccess(response, offerPasskeyRegister, authType);
      callback && callback();
    } catch (e: Error | string | any) {
      this.handleError(e, authType);
      return null;
    }
  }

  protected async loginWithoutMainFlow(url: string, fields: object, config?: AxiosRequestConfig): Promise<UiResponse | null> {
    this.resetState();
    const authType = url.includes('/email/auth') ? 'email' : undefined;
    try {
      const response = await API.post<UiResponse>(`${url}${this.routeStore.formForwardParams}`, fields, config);
      runInAction(() => {
        this.submitting = false;
      });
      return response.data;
    } catch (e: Error | string | any) {
      this.handleError(e, authType);
      return null;
    }
  }

  @actionBound
  public handleError(e: AxiosError<UiResponse>, authType?: string) {
    this.handleSensitiveData(e);
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.submitting = false;
    if (isAxiosError(e) && e.response?.data?.redirectUrl) {
      return this.routeStore.route(e.response.data.redirectUrl);
    }
    this.uiStore.showErrorNoticeFromResponse(e.response);
    if (authType) {
      GaDataStore.loginStatus(false, authType, this.rememberMeStore.rememberMe);
    }
  }

  @actionBound
  protected async handleSuccess(response: AxiosResponse<UiResponse>, offerPasskeyRegister: boolean, authType?: string) {
    const uiResponse: UiResponse = response.data;
    if (!uiResponse) {
      return;
    }
    if (!!uiResponse.params) {
      this.mergeStore.mergeParams = uiResponse.params;
      if (uiResponse.params.mergeStage === ProfileMapper.MERGE_ACCESS_VERIFIED) {
        this.submitting = false;
        return this.mergeStore.showMergeConfirmationOnAccountPage();
      }
    }
    if (offerPasskeyRegister && uiResponse.component === View.WEB_API) {
      await this.passkeyStore.handleOfferRegisterModalLogic(() => {
        this.handleSuccessContinue(response, uiResponse, authType);
      });
    } else {
      this.handleSuccessContinue(response, uiResponse, authType);
    }
  }

  private handleSuccessContinue(response: AxiosResponse<UiResponse>, uiResponse: UiResponse, authType?: string) {
    if (uiResponse.redirectUrl) {
      if (isSameProtocolAndHost(extractHyperlink(uiResponse.redirectUrl)?.href)) {
        this.submitting = false;
      }
      if (uiResponse.redirectUrl === Config.teliaWebUrl && uiResponse.component === View.ACCOUNT) {
        return this.uiStore.handleNotice(response, false);
      }
      this.routeStore.route(uiResponse.redirectUrl);
      uiResponse?.notice?.message && this.uiStore.showSuccessNotice(uiResponse.notice.message);
      return;
    }
    if (uiResponse.component === View.LARES) {
      this.routeStore.route(LARES_POST_FORM_PATH + stringify(uiResponse.params, { addQueryPrefix: true }));
    } else if (uiResponse.component === View.WEB_API) {
      this.routeStore.route(getPathForComponent(uiResponse) + this.routeStore.formForwardParams);
    } else if (uiResponse.component === View.ACCOUNT) {
      this.routeStore.route(getPathForComponent(uiResponse) + this.routeStore.preserveParams(['locale', 'app']));
    } else if (uiResponse.component) {
      this.routeStore.route(getPathForComponent(uiResponse) + this.routeStore.formForwardParams);
    }
    this.submitting = false;
    this.uiStore.handleNotice(response, false);
    if (authType) {
      GaDataStore.loginStatus(true, authType, this.rememberMeStore.rememberMe);
    }
  }

  handleSensitiveData = (e: AxiosError<UiResponse>) => {
    if (e.config?.data) {
      const data = JSON.parse(e.config.data);
      if (data?.password) {
        data.password = '***';
      }
      e.config.data = JSON.stringify(data);
    }
  };

  @actionBound
  private resetState() {
    this.uiStore.hideNotices();
    this.submitting = true;
  }
}
